import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { loadLocaleMessages, loadRuntimeCatalogues, loadMessageParameters } from './translations'
import { normalizeLocale, localeDirection, supportedLocales } from './config/locales'
import { loadCalendarLocale } from './config/calendarLocales'

i18n
  .use(initReactI18next)
  .init({
    // Entry points await setLocale before rendering. Keep catalogues out of
    // the eager application graph, including the English fallback.
    resources: {},
    lng: 'en', // Initialized to 'en' so that setLocale('ar') triggers a re-render
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false, // React already escapes values
    },
  })

let localeSequence = 0

export async function setLocale(locale: string): Promise<boolean> {
  const code = normalizeLocale(locale)
  if (!code) return false
  const sequence = ++localeSequence
  let catalogues: Record<string, Record<string, string>>
  let parameters: Record<string, Record<string, string>>
  let messages: Record<string, any> | undefined
  let en: Record<string, any> | undefined
  try {
    const loaded = await Promise.all([loadLocaleMessages(code), loadLocaleMessages('en'), loadRuntimeCatalogues(supportedLocales.map(language => language.code)), loadMessageParameters(), import('./validation/locale'), loadCalendarLocale(code)])
    ;[messages, en, catalogues, parameters] = loaded
  } catch { return false }
  if (sequence !== localeSequence) return false
  if (!messages || !en || typeof messages !== 'object' || Array.isArray(messages)) return false
  i18n.addResourceBundle('en', 'translation', en, true, true)
  const interfaceAliases: Record<string, string> = {}
  for (const [key, value] of Object.entries(en)) {
    if (typeof value === 'string' && typeof messages[key] === 'string' && !(value in messages)) interfaceAliases[value] = messages[key]
  }
  i18n.addResourceBundle(code, 'translation', { ...interfaceAliases, ...messages }, true, true)
  for (const [language, entries] of Object.entries(catalogues)) {
    i18n.addResourceBundle(language, 'runtimeMessages', entries, true, true)
    const aliases: Record<string, string> = { ...entries }
    for (const source of Object.values(catalogues)) {
      for (const [key, text] of Object.entries(source)) if (entries[key]) aliases[text] = entries[key]
    }
    i18n.addResourceBundle(language, 'translation', aliases, true, true)
  }
  i18n.addResourceBundle('en', 'messageParameters', parameters, true, true)
  await i18n.changeLanguage(code)
  if (sequence !== localeSequence) return false
  document.documentElement.lang = code
  document.documentElement.dir = localeDirection(code)
  document.body.classList.toggle('rtl', localeDirection(code) === 'rtl')
  persistLanguage(code)
  return true
}

// Safari private mode / quota-exceeded / policy-disabled storage throws on
// setItem; the language switch itself must still report success.
function persistLanguage(locale: string): void {
  try {
    localStorage.setItem('language', locale)
  } catch {
    console.warn(`localStorage unavailable — "${locale}" preference not persisted.`)
  }
}

export default i18n
